/* hs-eslint ignored failing-rules */
/* eslint-disable react-hooks/exhaustive-deps */

import UICardHeader from 'UIComponents/card/UICardHeader';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import AccordionTransition from 'UIComponents/transitions/AccordionTransition';
import { useContext, useId } from 'react';
import { currentOrderedPaidProductLevels } from 'self-service-api/constants/ProductLevels';
import { MARKETING } from 'self-service-api/constants/ProductLines';
import { CONTACT, USER } from 'self-service-api/constants/UnitTypes';
import styled from 'styled-components';
import { PlanBuilderContext } from '../../../context/PlanBuilderContext';
import { useAppSelector } from '../../../reducers';
import { bundledProductLinesForSkuIdSelector } from '../../../selectors/ProductConfigsSelectors';
import { productVersionsFromProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { currentProductVersionSelector, currentSkuIdForProductSelector, productForSkuIdSelector, quantityPackForProductVersionSelector, skuIdHasBundledProductsSelector, skuIdIsQuantityPackSelector, skuSelector } from '../../../selectors/ProductsSelectors';
import { seleniumUnitQuantitiesStringSelector, seleniumUnitsTypesStringSelector } from '../../../utils/planBuilderUtils/SeleniumUtils';
import QuantityPickerContainerLeftAll from '../QuantityPickerLeft/QuantityPickerContainerLeftAll';
import HubProductLevelPicker from './HubProductLevelPicker';
import PlanPickerHeader from './PlanPickerHeader';
import { useSetProductLevel } from './useSetProductLevel';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Container = styled(UICardWrapper).withConfig({
  displayName: "HubPlanPickerRowAll__Container",
  componentId: "sc-1tn0fn6-0"
})(["max-width:700px;"]);
const StyledCardHeader = styled(UICardHeader).withConfig({
  displayName: "HubPlanPickerRowAll__StyledCardHeader",
  componentId: "sc-1tn0fn6-1"
})(["& > div > span{padding-bottom:0;}"]);
const HubPlanPickerRowAll = ({
  productLine,
  className,
  compact
}) => {
  const {
    productConfigurations,
    setProductConfigurations,
    setIsLoading,
    isLoading,
    requiredProductLines,
    trackingSource,
    screen
  } = useContext(PlanBuilderContext);
  const required = requiredProductLines.includes(productLine);

  // do you own something from `productLine`
  const ownedSkuId = useAppSelector(state => {
    const ownedProductVersion = productVersionsFromProductInventorySelector(state).find(({
      skuId
    }) => {
      if (skuIdHasBundledProductsSelector(state, {
        skuId
      })) {
        const bundledProducts = bundledProductLinesForSkuIdSelector(state, {
          skuId
        });
        return bundledProducts.includes(productLine);
      }
      const product = productForSkuIdSelector(state, {
        skuId
      });
      return product.productLine === productLine && product.productLevel;
    });
    return ownedProductVersion && ownedProductVersion.skuId;
  });

  // if you own something from `productLine`, is it the current, latest version?
  const ownedCurrentSkuId = useAppSelector(state => {
    if (!ownedSkuId) return null;
    const {
      apiName: ownedApiName
    } = productForSkuIdSelector(state, {
      skuId: ownedSkuId
    });
    const {
      skuId: currentProductVersionSkuId
    } = currentProductVersionSelector(state, {
      apiName: ownedApiName
    });
    return ownedSkuId === currentProductVersionSkuId ? ownedSkuId : null;
  });

  //which sku for this productLine should be selected
  const selectedSkuId = useAppSelector(state => {
    const foundConfig = productConfigurations.find(({
      skuId
    }) => {
      const product = productForSkuIdSelector(state, {
        skuId
      });
      const hasBundleWithProductLine = skuIdHasBundledProductsSelector(state, {
        skuId
      }) && bundledProductLinesForSkuIdSelector(state, {
        skuId
      }).includes(productLine);
      return product.productLine === productLine && product.productLevel || hasBundleWithProductLine;
    });
    let updatedOwnedSkuId;
    if (ownedCurrentSkuId) {
      const {
        productId
      } = skuSelector(state, {
        skuId: ownedCurrentSkuId
      });
      updatedOwnedSkuId = ownedCurrentSkuId && currentSkuIdForProductSelector(state, {
        productId
      });
    }
    return foundConfig ? foundConfig.skuId : updatedOwnedSkuId;
  });
  const ownedProductLevel = useAppSelector(state => ownedCurrentSkuId ? productForSkuIdSelector(state, {
    skuId: ownedCurrentSkuId
  }).productLevel : null);

  // This can be different than ownedProductLevel if you own a legacy product (sidegrades)
  const disabledProductLevel = useAppSelector(state => {
    if (!ownedSkuId) return null;
    const {
      productLevel
    } = productForSkuIdSelector(state, {
      skuId: ownedSkuId
    });
    const currentOrderedPaidProductLevel = productLevel === currentOrderedPaidProductLevels[0] ? null : currentOrderedPaidProductLevels[currentOrderedPaidProductLevels.indexOf(productLevel) - 1];
    return currentOrderedPaidProductLevel;
  });
  const selectedProductLevel = useAppSelector(state => selectedSkuId ? productForSkuIdSelector(state, {
    skuId: selectedSkuId
  }).productLevel : null);
  const additionalUnitSkuIds = useAppSelector(state => {
    let skuIds = [];
    if (selectedSkuId) {
      const skuIdIsQuantityPack = skuIdIsQuantityPackSelector(state, {
        skuId: selectedSkuId
      });
      if (skuIdIsQuantityPack) {
        skuIds = [selectedSkuId];
      }
      const quantityPack = quantityPackForProductVersionSelector(state, {
        productVersionSkuId: selectedSkuId,
        unitType: productLine === MARKETING ? CONTACT : USER,
        unitScope: productLine
      });
      if (quantityPack) {
        skuIds = [quantityPack.skuId];
      }
    }
    return skuIds;
  });
  const seleniumUnitsString = useAppSelector(state => seleniumUnitQuantitiesStringSelector(state, {
    productConfigurations,
    productVersionSkuId: selectedSkuId
  }));
  const seleniumUnitsTypesIdString = useAppSelector(state => seleniumUnitsTypesStringSelector(state, {
    productConfigurations,
    productVersionSkuId: selectedSkuId
  }));
  const apiName = useAppSelector(state => {
    const product = selectedSkuId && productForSkuIdSelector(state, {
      skuId: selectedSkuId
    });
    return product && product.apiName;
  });
  const hubProductLevelPickerLabelId = useId();
  const setProductLevel = useSetProductLevel({
    productConfigurations,
    productLine,
    setProductConfigurations,
    ownedProductLevel,
    screen,
    setIsLoading,
    selectedProductLevel,
    trackingSource
  });
  const Wrapper = compact ? 'div' : Container;
  const SectionContainer = compact ? 'div' : UICardSection;
  const PlanPickerTitle = /*#__PURE__*/_jsx(PlanPickerHeader, {
    productLine: productLine
    // trackingSource={trackingSource}
    ,
    id: hubProductLevelPickerLabelId
  });
  return /*#__PURE__*/_jsxs(Wrapper, {
    className: className,
    "data-product-level": selectedProductLevel,
    "data-selenium-id": `${productLine}-plan-builder-picker`,
    "data-api-name": apiName,
    "data-units": seleniumUnitsString || '0',
    "data-units-type": seleniumUnitsTypesIdString,
    children: [compact ? /*#__PURE__*/_jsx("div", {
      children: PlanPickerTitle
    }) : /*#__PURE__*/_jsx(StyledCardHeader, {
      titleSize: "xs",
      title: PlanPickerTitle
    }), /*#__PURE__*/_jsxs(SectionContainer, {
      children: [/*#__PURE__*/_jsx(HubProductLevelPicker, {
        ariaLabelledById: hubProductLevelPickerLabelId,
        productLine: productLine,
        required: required,
        selectedProductLevel: selectedProductLevel || null,
        ownedProductLevel: ownedProductLevel,
        disabledProductLevel: disabledProductLevel,
        setSelectedProductLevel: setProductLevel,
        isLoading: isLoading
      }), /*#__PURE__*/_jsx(AccordionTransition, {
        open: additionalUnitSkuIds.length > 0,
        children: /*#__PURE__*/_jsx(QuantityPickerContainerLeftAll, {
          additionalUnitSkuIds: additionalUnitSkuIds,
          setProductLevel: setProductLevel,
          trackingSource: trackingSource
        })
      })]
    })]
  });
};
export default HubPlanPickerRowAll;